import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import type { AsyncPaginateProps, LoadOptions } from 'react-select-async-paginate';
import { components } from 'react-select';
import type { GroupBase, StylesConfig, OptionsOrGroups } from 'react-select';
import { useTheme } from '../middleware/ThemeContext';
import StyledText from './StyledComponents/StyledText';
import StyledCheckBox from './StyledComponents/CheckBox/StyledCheckBox';
import { Button } from './form/Button';
import type { Theme } from '../utils/themes';
import { CorporateTheme, CreateFormTheme, font } from '../utils/themes';
import PlaceholderImage from '../assets/images/placeholder-image.jpg';
import Badge from './Badge';
import { statusColorMapping } from '../utils/stausMapping';
import { authFetch } from '../utils/authFetch';
import styled from 'styled-components';

// Fallback option shape if caller does not provide one
export type DefaultOption = {
    label: string;
    value: string | number;
    icon?: React.ReactNode;
    [key: string]: unknown;
};

type MapResponse<Option> = (json: any, page: number) => {
    options: Option[];
    hasMore: boolean;
    additional?: Record<string, unknown>;
};

export type AMSSelectProps<
    Option = DefaultOption,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
    Additional extends { page: number } = { page: number }
> = Omit<AsyncPaginateProps<Option, Group, Additional, IsMulti>, 'loadOptions' | 'additional'> & {
    label?: string;
    error?: string;
    endpoint?: string;
    /** Optional API base. Defaults to process.env.REACT_APP_API_URL + "/api" if present, else window origin. */
    baseUrl?: string;
    /** Show option accessKey under the label. */
    includeId?: boolean;
    includeAccessKey?: boolean;
    /** Show option image (uses signedUrl or image). */
    includeImage?: boolean;
    /** Extra query params sent with every request */
    additionalParams?: Record<string, string | number | boolean | null | undefined>;
    /** Custom mapping from API response to options + hasMore */
    mapResponse?: MapResponse<Option>;
    /** Query string keys. Defaults: search=, page=, limit= */
    queryParamKey?: string;
    pageParamKey?: string;
    limitParamKey?: string;
    limit?: number;
    /** Extra fetch config (headers, method overrides, etc.) */
    requestInit?: RequestInit;
    /** Show an add button when no results are found */
    includeAddButton?: boolean;
    /** Callback when user clicks add in empty state */
    onAddButtonClick?: (term: string) => void;
    /** Custom text for the add button. Defaults to "Add". */
    addButtonText?: string;
    /** Render optional icon nodes provided on each option (expects option.icon). */
    showIcons?: boolean;
    /** Show data type badge for single select options. */
    showDataType?: boolean;
    /** When true (default), automatically attaches auth headers via authFetch. */
    useAuthFetch?: boolean;
    /** When provided, bypasses network call and uses these options directly. */
    staticOptions?: Option[];
    /** Optional filter callback for static options search. */
    staticFilter?: (option: Option, searchTerm: string) => boolean;
    fontFamily?: string;
};

const buildStyles = <
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,

>(theme: Theme, hasError: boolean, isDisabled?: boolean, fontFamily?: string): StylesConfig<Option, IsMulti, Group> => ({
    control: (provided, state) => ({
        ...provided,
        minHeight: 42,
        height: 42,
        maxHeight: 42,
        backgroundColor: theme.CreateFormTheme.colors.inputBackground,
        borderRadius: '5px',
        borderColor: hasError
            ? theme.CreateFormTheme.colors.error
            : state.isFocused
                ? theme.CorporateTheme.colors.primary
                : theme.CorporateTheme.colors.border,
        boxShadow: 'none',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.65 : 1,
        '&:hover': {
            borderColor: hasError
                ? theme.CreateFormTheme.colors.error
                : theme.CreateFormTheme.colors.inputBorders,
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0px 10px',
        fontFamily: font.raleway,
        flexWrap: 'nowrap',
        overflow: 'hidden',
        textAlign: 'left',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: CreateFormTheme.colors.placeHolder,
        fontFamily: font.raleway,
        fontSize: '14px',
    }),
    input: (provided) => ({
        ...provided,
        color: 'CorporateTheme.colors.textSecondary',
        fontFamily: font.raleway,
        fontSize: '14px',
        flex: 1,
        minWidth: 80,
        width: 'auto',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),
    option: (provided, state) => ({
        ...provided,
        padding: 0,
        backgroundColor: state.isSelected
            ? theme.CorporateTheme.colors.background
            : state.isFocused
                ? theme.CreateFormTheme.colors.headerBG
                : theme.CorporateTheme.colors.background,
        color: theme.CorporateTheme.colors.textSecondary,
        cursor: 'pointer',
        fontFamily: font.raleway,
        fontSize: '14px',

    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '5px',
        // backgroundColor: theme.CorporateTheme.colors.background,
         backgroundColor: CorporateTheme.colors.white,

        marginTop: 6,
        padding:'10px'

    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,

    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: theme.CorporateTheme.colors.textSecondary,
        fontFamily: fontFamily || font.raleway,
        fontWeight: 500,
        fontSize: '14px',

    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: theme.CorporateTheme.colors.lightSecondary,
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: theme.CorporateTheme.colors.textSecondary,
        fontFamily: font.raleway,
        fontSize: '14px',
    }),
    multiValueRemove: (provided) => ({
        ...provided,
        ':hover': {
            backgroundColor: theme.CreateFormTheme.colors.headerBG,
            color: theme.CorporateTheme.colors.textSecondary,
        },
    }),
    menuList: (provided) => ({
        ...provided,
        padding: 0,
    }),
});

function defaultMapResponse<Option>(json: any, page: number, limit: number) {
    const items = json?.items
        ?? json?.options
        ?? json?.data
        ?? json?.results
        ?? json?.organizations
        ?? [];
    const totalCount = json?.totalCount ?? json?.pagination?.total ?? 0;
    const perPage = json?.perPage ?? json?.pagination?.perPage ?? limit;
    const currentPage = json?.page ?? json?.pagination?.page ?? page;

    const hasMore = totalCount
        ? currentPage * perPage < totalCount
        : Array.isArray(items) && items.length === perPage;

    const normalized = (items as any[]).map((item, idx) => ({
        // Ensure react-select sees label/value even if API does not provide them
        label: item?.label ?? item?.name ?? String(item?.id ?? ''),
        value: item?.value ?? item?.id,
        // Internal identifiers to ensure unique keys across pages/results
        __idx: idx,
        __page: currentPage,
        ...item,
    }));

    return {
        options: normalized as Option[],
        hasMore,
        additional: { page: currentPage + 1 },
    };
}

function AMSSelect<
    Option extends DefaultOption = DefaultOption,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
    Additional extends { page: number } = { page: number }
>(props: AMSSelectProps<Option, IsMulti, Group, Additional>) {
    const {
        label,
        error,
        styles,
        menuPortalTarget,
        placeholder = 'Select...',
        loadingMessage,
        isDisabled,
        endpoint,
        additionalParams,
        mapResponse,
        queryParamKey = 'query',
        pageParamKey = 'page',
        limitParamKey = 'perPage',
        limit = 50,
        requestInit,
        debounceTimeout = 400,
        defaultOptions = true,
        baseUrl,
        includeId = false,
        includeImage = false,
        includeAccessKey = false,
        includeAddButton = false,
        onAddButtonClick,
        addButtonText = 'Add',
        showIcons = false,
        showDataType = false,
        useAuthFetch = true,
        staticOptions,
        staticFilter,
        fontFamily,
        ...restProps
    } = props;
    const { theme } = useTheme();

    // Pull out user supplied component overrides so we can merge ours
    const {
        components: userComponents,
        onChange: userOnChange,
        onInputChange: userOnInputChange,
        onMenuClose: userOnMenuClose,
        ...asyncProps
    } = restProps as AMSSelectProps<Option, IsMulti, Group, Additional> & { components?: any; onChange?: any; onInputChange?: any; onMenuClose?: any };
    const isMultiSelect = Boolean(asyncProps.isMulti);
    const selectRef = useRef<any>(null);
    const [inputValue, setInputValue] = useState('');
    const [isFetchingOptions, setIsFetchingOptions] = useState(false);
    const pendingRequestsRef = useRef(0);

    // Prefer portaling the menu into the active modal (if any) to avoid
    // interacting with the page/body scrollbars when used inside dialogs.
    const resolvedMenuPortalTarget = useMemo(() => {
        if (menuPortalTarget) return menuPortalTarget as HTMLElement;

        if (typeof document === 'undefined') return null;

        const modalContent = document.querySelector('.modal.show .modal-content') as HTMLElement | null;
        if (modalContent) return modalContent;

        return document.body;
    }, [menuPortalTarget]);

    const baseStyles = useMemo(
        () => buildStyles<Option, IsMulti, Group>(theme, Boolean(error), isDisabled, fontFamily),
        [theme, error, isDisabled, fontFamily]
    );

    const mergedStyles = useMemo(
        () => (styles ? { ...baseStyles, ...styles } : baseStyles),
        [baseStyles, styles]
    );

    const resolvedStaticOptions = useMemo(() => {
        if (!staticOptions) return null;
        return staticOptions.map((option) => ({ ...option }));
    }, [staticOptions]);

    const effectiveDebounceTimeout = resolvedStaticOptions ? 0 : debounceTimeout;

    const filterStaticOptions = useCallback(
        (opts: Option[], searchTerm: string) => {
            if (!searchTerm) return opts;
            if (staticFilter) {
                return opts.filter((opt) => staticFilter(opt, searchTerm));
            }
            const normalized = searchTerm.toLowerCase();
            return opts.filter((opt: any) => {
                const haystack = [
                    opt?.label,
                    opt?.value,
                    opt?.accessKey,
                    opt?.id,
                ]
                    .filter(Boolean)
                    .map(String)
                    .join(' ')
                    .toLowerCase();

                return haystack.includes(normalized);
            });
        },
        [staticFilter]
    );

    const handleChange = useCallback(
        (value: any, actionMeta: any) => {
            userOnChange?.(value, actionMeta);

            if (isMultiSelect) {
                // Re-focus input so users can keep typing after selecting.
                setTimeout(() => {
                    selectRef.current?.focus?.();
                }, 0);
            }
        },
        [isMultiSelect, userOnChange]
    );

    const mergedAsyncProps = useMemo(
        () => ({
            // Keep the dropdown open for multi-select to allow selecting multiple options in one search.
            closeMenuOnSelect: !isMultiSelect,
            ...(isMultiSelect
                ? {
                    // Critical for multi-select UX: keep focus + menu open after selection.
                    blurInputOnSelect: false,
                    openMenuOnFocus: true,
                }
                : {}),
            hideSelectedOptions: false,
            onChange: handleChange,
            onMenuClose: (...args: any[]) => {
                // Requirement: once the dropdown closes, clear the search box.
                if (isMultiSelect) setInputValue('');
                userOnMenuClose?.(...args);
            },
            // Prevent accidental removal of the last selection when the input is empty.
            backspaceRemovesValue: !isMultiSelect,
            inputValue,
            onInputChange: (value: string, actionMeta: any) => {
                const nextValue = value ?? '';
                const action = actionMeta?.action as string | undefined;

                // react-select fires `set-value` (and sometimes `menu-close`/`input-blur`) with empty string
                // after selecting an option; for multi-select we want to keep the current search term.
                if (isMultiSelect && (action === 'set-value' || action === 'menu-close' || action === 'input-blur')) {
                    return userOnInputChange
                        ? userOnInputChange(inputValue, actionMeta)
                        : inputValue;
                }

                setInputValue(nextValue);
                return userOnInputChange ? userOnInputChange(nextValue, actionMeta) : nextValue;
            },
            // Ensure option keys are unique, even if backend returns duplicate ids/values.
            // Users can override via props; this is a safe default.
            getOptionValue: (opt: any) => {
                const base = opt?.id ?? opt?.value ?? opt?.label ?? '';
                const idx = (opt as any)?.__idx;
                const pg = (opt as any)?.__page;
                const safeBase = String(base);
                const suffix = [pg, idx].filter((v) => v !== undefined && v !== null).map(String).join('-');
                return suffix ? `${safeBase}-${suffix}` : safeBase || `__opt-${Math.random().toString(36).slice(2)}`;
            },
            ...asyncProps,
        }),
        [asyncProps, handleChange, inputValue, isMultiSelect, userOnInputChange, userOnMenuClose]
    );

    const resolvedLoadingMessage = loadingMessage ?? (() => 'Loading...');

    const CustomOption = useCallback((optionProps: any) => {
        const { data } = optionProps;
        const labelText = data?.label ?? '';
        const idText = includeId ? (data?.id ?? data?.value ?? '') : null;
        const accessKeyText = includeAccessKey ? (data?.accessKey ?? '') : null;
        const imageSrc = includeImage ? (data?.signedUrl ?? data?.image ?? data?.avatar) : null;
        const iconNode = showIcons ? data?.icon : null;
        const isChecked = optionProps.isSelected;
        const optionsList = (optionProps?.selectProps?.options ?? []) as any[];
        const lastOption = Array.isArray(optionsList) && optionsList.length > 0
            ? optionsList[optionsList.length - 1]
            : null;
        const isLastOption = lastOption && (lastOption?.value ?? lastOption?.id) === (data?.value ?? data?.id);
        // const checkboxBorderColor = isChecked
        //     ? theme.CorporateTheme.colors.primary
        //     : theme.CreateFormTheme.colors.border;
        // const checkboxBgColor = theme.CorporateTheme.colors.primary;
         const checkboxBorderColor = isChecked
            ? CorporateTheme.colors.primary
            : CreateFormTheme.colors.headerColor;
        const checkboxBgColor = CorporateTheme.colors.primary;



        const handleCheckboxToggle = (
            event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
        ) => {
            event.preventDefault();
            event.stopPropagation();
            optionProps.selectOption?.(data);
        };

        const customOnClick = isMultiSelect ? undefined : optionProps.onClick;

        return (
            <components.Option {...optionProps} onClick={customOnClick}>
                <div style={{ padding: '4px 10px', background: theme.CorporateTheme.colors.white }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center',
                            padding: '4px 0px',
                            // borderBottom: isLastOption ? 'none' : `1px solid ${theme.CreateFormTheme.colors.border}`,
                            background: theme.CorporateTheme.colors.white,
                            cursor: isMultiSelect ? 'pointer' : 'inherit',
                        }}
                        onClick={isMultiSelect ? handleCheckboxToggle : undefined}
                    >
                        {isMultiSelect ? (
                            <StyledCheckBox
                                value={isChecked}
                                onChange={handleCheckboxToggle}
                                onClick={handleCheckboxToggle}
                                size={16}
                                borderColor={checkboxBorderColor}
                                bgColor={checkboxBgColor}
                                checkColor={CorporateTheme.colors.background}
                                color={CreateFormTheme.colors.textSecondary}
                                className="w-auto me-0 me-sm-0 flex-shrink-0"
                            />
                        ) : null}
                        {includeImage ? (
                            <div
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    background: theme.CorporateTheme.colors.headerBG,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    color: theme.CorporateTheme.colors.textSecondary,
                                    flexShrink: 0,
                                }}
                            >
                                {imageSrc ? (
                                    <img
                                        src={imageSrc}
                                        alt={labelText}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = PlaceholderImage;
                                        }}
                                    />
                                ) : (
                                    (labelText || '').slice(0, 1).toUpperCase()
                                )}
                            </div>
                        ) : null}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    {iconNode ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {iconNode}
                                        </span>
                                    ) : null}
                                    <StyledText
                                        fontSize="14px"
                                        fontWeight={600}
                                        textColor={CreateFormTheme.colors.textSecondary}
                                        fontFamily={fontFamily || font.raleway}
                                        style={{ lineHeight: '18px' }}
                                    >
                                        {labelText}
                                    </StyledText>
                                </div>

                                {showDataType && data?.dataType ? (
                                    <FlagBadge
                                        $circleColor={CorporateTheme.colors.white}
                                        background={CorporateTheme.colors.count}
                                        style={{ marginLeft: 'auto', flexShrink: 0 }}
                                    >
                                        {data.dataType}
                                    </FlagBadge>
                                ) : null}
                            </div>
                            {idText ? (
                                <StyledText
                                    fontSize="14px"
                                    fontWeight={600}
                                    textColor={theme.CreateFormTheme.colors.placeHolder}
                                    fontFamily={font.raleway}
                                    style={{ lineHeight: '16px' }}
                                >
                                    Id: {idText}
                                </StyledText>
                            ) : null}
                            {accessKeyText ? (
                                <StyledText
                                    fontSize="14px"
                                    fontWeight={600}
                                    textColor={theme.CreateFormTheme.colors.placeHolder}
                                    fontFamily={font.raleway}
                                    style={{ lineHeight: '16px' }}
                                >
                                    {accessKeyText}
                                </StyledText>
                            ) : null}
                        </div>
                    </div>
                </div>
            </components.Option>
        );
    }, [includeId, includeImage, isMultiSelect, showIcons, includeAccessKey, showDataType, theme]);

    const CountValueContainer = useCallback((valueProps: any) => {
        const { children, ...restProps } = valueProps;
        const value = valueProps?.selectProps?.value;
        const isOpen = Boolean(valueProps?.selectProps?.menuIsOpen);
        const count = Array.isArray(value) ? value.length : value ? 1 : 0;
        const showCount = isMultiSelect && count > 0 && !isOpen;

        return (
            <components.ValueContainer {...restProps}>
                {showCount ? (
                    <span
                        style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: CorporateTheme.colors.textSecondary,
                            marginRight: 8,
                            display: 'inline-flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {`${count} Selected`}
                    </span>
                ) : null}
                {children}
            </components.ValueContainer>
        );
    }, [isMultiSelect, theme]);

    const NoOptionsMessage = useCallback((messageProps: any) => {
        const search = (messageProps?.selectProps?.inputValue || '').trim();
        const isLoading = isFetchingOptions || Boolean(messageProps?.selectProps?.isLoading);
        const canAdd = includeAddButton && Boolean(search) && typeof onAddButtonClick === 'function' && !isLoading;
        const messageText = canAdd
            ? `No result found for '${search}'. Try adding this.`
            : 'No result found';

        const loadingLabel = typeof messageProps?.selectProps?.loadingMessage === 'function'
            ? messageProps.selectProps.loadingMessage()
            : 'Loading...';

        if (isLoading) {
            return (
                <components.NoOptionsMessage {...messageProps}>
                    <div style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <StyledText fontSize="14px" fontWeight={700} textColor={theme.CorporateTheme.colors.textSecondary}>
                            {loadingLabel}
                        </StyledText>
                    </div>
                </components.NoOptionsMessage>
            );
        }

        return (
            <components.NoOptionsMessage {...messageProps}>
                <div style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <StyledText fontSize="14px" fontWeight={700} textColor={theme.CorporateTheme.colors.textSecondary}>
                        {messageText}
                    </StyledText>
                    {canAdd ? (
                        <Button
                            theme={theme}
                            type="button"
                            $variant="add"
                            onClick={() => {
                                onAddButtonClick?.(search);
                                selectRef.current?.blur();
                            }}
                            disabled={!search}
                            fontSize="12px"
                            style={{ marginTop: 10, padding: '8px 12px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', marginLeft: 'auto', marginRight: 'auto' }}
                        >
                            {addButtonText}
                        </Button>
                    ) : null}
                </div>
            </components.NoOptionsMessage>
        );
    }, [addButtonText, includeAddButton, isFetchingOptions, onAddButtonClick, theme]);

    const ClearAllIndicator = useCallback((clearProps: any) => {
        const { selectProps } = clearProps;
        const value = selectProps?.value;
        const isOpen = Boolean(selectProps?.menuIsOpen);
        const count = Array.isArray(value) ? value.length : value ? 1 : 0;
        const labelText = `${count} Selected`;
        if (!isMultiSelect || count === 0) return null;
        return (
            <span>
                {isOpen ? (
                    <Badge bgColor={statusColorMapping.PUBLISHED} text={labelText} color={theme.CorporateTheme.colors.white} fontWeight={500} fontSize='14px' borderRadius='38px' />
                ) : (
                    <components.ClearIndicator {...clearProps} />
                )}
            </span>
        );
    }, [isMultiSelect, theme]);

    const buildRequestUrl = useCallback(
        (endpointPath: string) => {
            const envBase = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/portal` : undefined;
            const base = baseUrl || envBase || (typeof window !== 'undefined' ? window.location.origin : '');

            // Avoid losing path segments by stripping leading slash on endpoint
            const endpointClean = endpointPath.startsWith('/') ? endpointPath.slice(1) : endpointPath;
            const baseWithSlash = base.endsWith('/') ? base : `${base}/`;
            return new URL(endpointClean, baseWithSlash);
        },
        [baseUrl]
    );

    const loadOptions = useCallback<LoadOptions<Option, Group, Additional>>(
        async (
            search: string,
            _loadedOptions: OptionsOrGroups<Option, Group>,
            additional: Additional | undefined
        ) => {
            const page = (additional as Additional | undefined)?.page ?? 1;

            if (resolvedStaticOptions) {
                const filtered = filterStaticOptions(resolvedStaticOptions, search ?? '');
                return {
                    options: filtered,
                    hasMore: false,
                    additional: { page: 1 } as Additional,
                };
            }

            if (!endpoint) {
                console.error('AMSSelect: endpoint is required when staticOptions are not provided.');
                return {
                    options: [],
                    hasMore: false,
                    additional: { page: 1 } as Additional,
                };
            }

            try {
                pendingRequestsRef.current += 1;
                setIsFetchingOptions(true);
                const url = buildRequestUrl(endpoint);
                const params = new URLSearchParams();

                if (additionalParams) {
                    Object.entries(additionalParams).forEach(([key, value]) => {
                        if (value !== undefined && value !== null) params.set(key, String(value));
                    });
                }

                if (search) params.set(queryParamKey, search);
                params.set(pageParamKey, String(page));
                params.set(limitParamKey, String(limit));

                url.search = params.toString();

                const fetchFn = useAuthFetch ? authFetch : fetch;
                const response = await fetchFn(url.toString(), {
                    method: 'GET',
                    ...requestInit,
                });

                if (!response.ok) throw new Error(`AMSSelect request failed: ${response.status}`);

                const json = await response.json();
                const mapped = mapResponse
                    ? mapResponse(json, page)
                    : defaultMapResponse<Option>(json, page, limit);

                return {
                    options: mapped.options,
                    hasMore: mapped.hasMore,
                    additional: (mapped.additional ?? { page: page + 1 }) as Additional,
                };
            } catch (err) {
                console.error('AMSSelect loadOptions error', err);
                return {
                    options: [],
                    hasMore: false,
                    additional: { page: 1 } as Additional,
                };
            } finally {
                pendingRequestsRef.current -= 1;
                if (pendingRequestsRef.current <= 0) {
                    pendingRequestsRef.current = 0;
                    setIsFetchingOptions(false);
                }
            }
        },
        [resolvedStaticOptions, filterStaticOptions, endpoint, additionalParams, queryParamKey, pageParamKey, limitParamKey, limit, requestInit, mapResponse, useAuthFetch]
    );

    const mergedComponents = useMemo(
        () => ({
            Option: CustomOption,
            ...(isMultiSelect
                ? {
                    ValueContainer: CountValueContainer,
                    ClearIndicator: ClearAllIndicator,
                    MultiValue: () => null,
                    MultiValueContainer: () => null,
                    MultiValueRemove: () => null,
                    MultiValueLabel: () => null,
                }
                : {}),
            NoOptionsMessage,
            ...userComponents,
        }),
        [ClearAllIndicator, CountValueContainer, CustomOption, NoOptionsMessage, isMultiSelect, userComponents]
    );

    return (
        <div className="ams-select">
            {label ? (
                <StyledText
                    htmlTag="label"
                    fontSize="13px"
                    fontWeight={600}
                    textColor={theme.CorporateTheme.colors.textSecondary}
                    fontFamily={font.raleway}
                    style={{ display: 'block', marginBottom: '6px' }}
                >
                    {label}
                </StyledText>
            ) : null}

            <AsyncPaginate
                selectRef={selectRef}
                styles={mergedStyles}
                defaultOptions={defaultOptions}
                placeholder={placeholder}
                menuPortalTarget={resolvedMenuPortalTarget}
                menuPosition={resolvedMenuPortalTarget ? 'fixed' : 'absolute'}
                menuShouldScrollIntoView={false}
                loadingMessage={resolvedLoadingMessage}
                isDisabled={isDisabled}
                menuPlacement="auto"
                loadOptions={loadOptions}
                debounceTimeout={effectiveDebounceTimeout}
                additional={{ page: 1 } as Additional}
                components={mergedComponents}
                {...mergedAsyncProps}
            />

            {error ? (
                <StyledText
                    fontSize="12px"
                    fontWeight={500}
                    textColor={theme.CreateFormTheme.colors.error}
                    fontFamily={font.raleway}
                    style={{ marginTop: '6px', display: 'block' }}
                >
                    {error}
                </StyledText>
            ) : null}
        </div>
    );
}

const FlagBadge = styled.span<{ $circleColor?: string, background?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: "Raleway", sans-serif;
  background: ${({ background }) => background || '#ffebee'};
  color: ${({ $circleColor }) => $circleColor || '#009828'};
  text-transform: uppercase;
`;

export default AMSSelect;
